import { Offer } from '../types';
import { QuantityControl } from './QuantityControl';
import { useCartStore } from '../stores/useCartStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useCurrencySymbol } from '../lib/currency';
import { Plus, Sparkles, Gift } from 'lucide-react';
import { getFullImageUrl } from '../lib/api';

interface OfferCardProps {
  offer: Offer;
  scheduledDate?: string; // ISO date string for scheduled orders
}

export function OfferCard({ offer, scheduledDate }: OfferCardProps) {
  const {
    addOffer,
    offers,
    updateOfferQuantity,
    addScheduledOffer,
    scheduledOrders,
    updateScheduledOfferQuantity
  } = useCartStore();
  const { isOpen } = useAuthStore();
  const currencySymbol = useCurrencySymbol();

  // Get quantity based on whether this is a scheduled or immediate order
  const quantity = scheduledDate
    ? scheduledOrders
      .find(order => order.scheduledDate === scheduledDate)
      ?.offers.find(item => item.offer.uuid === offer.uuid)
      ?.quantity || 0
    : offers.find(item => item.offer.uuid === offer.uuid)?.quantity || 0;
  const isZeroPriceOffer = offer.price === 0.0;

  // Calculate max offers available based on product stock
  const maxOffers = Math.min(
    ...offer.products.map(item => Math.floor(item.product.stock / item.quantity))
  );
  const hasStock = maxOffers > 0;

  const handleAddToCart = () => {
    if (scheduledDate) {
      addScheduledOffer(offer, scheduledDate, 1);
    } else {
      addOffer(offer, 1);
    }
  };

  const handleIncrease = () => {
    if (scheduledDate) {
      updateScheduledOfferQuantity(offer.uuid, scheduledDate, quantity + 1);
    } else {
      updateOfferQuantity(offer.uuid, quantity + 1);
    }
  };

  const handleDecrease = () => {
    if (quantity > 0) {
      if (scheduledDate) {
        updateScheduledOfferQuantity(offer.uuid, scheduledDate, quantity - 1);
      } else {
        updateOfferQuantity(offer.uuid, quantity - 1);
      }
    }
  };

  return (
    <div className={`bg-gradient-to-br from-sky-100 via-sky-50 to-sky-100 dark:from-sky-900/30 dark:via-sky-800/20 dark:to-sky-900/30 rounded-2xl shadow-lg border-2 border-sky-200 dark:border-sky-700 overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.02] relative ${isZeroPriceOffer ? 'h-auto' : '' // Adjust height for zero-price offers
      }`}>
      {/* Sparkle decoration */}
      {!isZeroPriceOffer && (
        <div className="absolute top-3 left-3 w-8 h-8 bg-gradient-to-r from-yellow-400 to-sky-400 rounded-full flex items-center justify-center shadow-lg">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
      )}

      {isZeroPriceOffer ? (
        // Display only image and name for zero-price offers
        <>
          <div className="aspect-[4/3] overflow-hidden flex items-center justify-center relative">
            <img
              src={getFullImageUrl(offer.image_url || offer.image)}
              alt={offer.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-5">
            <h3 className="font-bold text-lg text-sky-900 dark:text-sky-100 leading-tight">
              {offer.name}
            </h3>
            {/* Add products list for zero-price offers */}
            <div className="mt-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-3">
              <p className="text-sm font-semibold text-sky-700 dark:text-sky-300 mb-2 flex items-center gap-2">
                <Gift className="w-4 h-4" />
                Incluye:
              </p>
              <div className="space-y-1">
                {offer.products.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-xs text-sky-600 dark:text-sky-400 font-medium">
                      • {item.quantity}x {item.product.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        // Display the regular offer card with enhanced product display
        <>
          <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-sky-200 to-sky-200 dark:from-sky-800/40 dark:to-sky-800/40 flex items-center justify-center relative">
            <img
              src={getFullImageUrl(offer.image_url || offer.image)}
              alt={offer.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4">
              <div className="bg-yellow-400 text-yellow-950 px-3 py-1 rounded-full text-xs font-black shadow-lg flex items-center gap-1 uppercase tracking-tighter">
                <Sparkles className="w-3 h-3" />
                Oferta Especial
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-2 right-2 w-6 h-6 bg-yellow-400 rounded-full opacity-70"></div>
            <div className="absolute bottom-4 left-4 w-4 h-4 bg-sky-400 rounded-full opacity-60"></div>
          </div>

          <div className="p-5">
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-bold text-lg text-sky-900 dark:text-sky-100 leading-tight">
                {offer.name}
              </h3>
              <div className="bg-gradient-to-r from-sky-500 to-sky-500 text-white px-3 py-1 rounded-full">
                <span className="text-xl font-bold">{currencySymbol}{offer.price.toFixed(2)}</span>
              </div>
            </div>

            {/* Enhanced products display */}
            <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-4 mb-5">
              <p className="text-sm font-semibold text-sky-700 dark:text-sky-300 mb-3 flex items-center gap-2">
                <Gift className="w-4 h-4" />
                Incluye ({offer.products_length} productos):
              </p>
              <div className="grid grid-cols-1 gap-2">
                {offer.products.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-sky-50 dark:bg-sky-900/20 rounded-lg p-2">
                    <div className="w-8 h-8 bg-sky-200 dark:bg-sky-700 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-sky-700 dark:text-sky-300">{item.quantity}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-sky-800 dark:text-sky-200 truncate">
                        {item.product.name}
                      </p>
                      <p className="text-xs text-sky-600 dark:text-sky-400">
                        {currencySymbol}{item.product.price.toFixed(2)} c/u
                      </p>
                    </div>
                    {item.product.image && item.product.image !== 'http://127.0.0.1:8000/media/products/Default.png' && (
                      <img
                        src={getFullImageUrl(item.product.image)}
                        alt={item.product.name}
                        className="w-8 h-8 object-cover rounded-full flex-shrink-0"
                      />
                    )}
                  </div>
                ))}
              </div>
              {/* Show total savings */}

            </div>

            <div className="flex items-center justify-between">
              {quantity > 0 ? (
                <QuantityControl
                  quantity={quantity}
                  onIncrease={handleIncrease}
                  onDecrease={handleDecrease}
                  max={maxOffers}
                />
              ) : (
                <div className="flex items-center gap-2 text-sky-400">
                  <span className="text-xs">-</span>
                  <span className="w-8 text-center font-semibold">0</span>
                  <span className="text-xs">+</span>
                </div>
              )}

              <button
                onClick={handleAddToCart}
                disabled={!hasStock || (!isOpen && !scheduledDate)}
                className={`bg-gradient-to-r from-sky-500 to-sky-500 hover:from-sky-600 hover:to-sky-600 text-white px-5 py-3 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105 ${(!hasStock || (!isOpen && !scheduledDate)) ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
              >
                <Plus className="w-4 h-4" />
                {(!isOpen && !scheduledDate) ? 'Local Cerrado' : (!hasStock ? 'Sin Stock' : 'Agregar Oferta')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}